import React, { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import timezoneList from 'timezone-list';
import { useResizeDetector } from 'react-resize-detector';
import { Content, Dialogs, Flex, Label, Popup, PropertyGrid, Selector, TabPane, Text, Toggle } from '@leverege/ui-elements';
import { OperatingHours, WeeklyHours } from '@leverege/operating-hours';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelEditorFactory } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import StringUtil from '@leverege/string-util';
import AddModeDialog from "./AddModeDialog.js";
import DeleteModeDialog from "./DeleteModeDialog.js";
import OverridesEditor from "./OverridesEditor.js";
import S from './OperatingHoursEditor.css';
const {
  t,
  tIcon
} = I18N.ns('imagine.operatingHours.OperatingHoursEditor');
const MIN_TABBED_WIDTH = 850;
function getDefaultCurrentMode(value) {
  const currentSet = OperatingHours.getCurrentMode(value);
  if (currentSet) {
    return currentSet;
  }
  const modes = OperatingHours.getModeEntries(value);
  if (modes.length > 0) {
    return modes[0][0];
  }
  return null;
}
export default function OperatingHoursEditor(props) {
  const {
    value,
    blueprint,
    onChange: pOnChange
  } = props;
  const {
    width,
    ref
  } = useResizeDetector();
  const onChange = useValueChange(OperatingHours, props);
  const [currentMode, setCurrentMode] = useState(getDefaultCurrentMode(value));
  const onModeChange = useCallback(({
    value: val
  }) => {
    setCurrentMode(val);
  }, []);
  const onCurrentModeChange = useCallback(evt => {
    onChange({
      data: 'setCurrentMode',
      value: evt.data
    });
  }, [onChange]);
  const modes = OperatingHours.getModes(value);
  const timezones = useMemo(() => {
    return timezoneList.getTimezones().sort((a, b) => {
      return a > b ? 1 : -1;
    }).map(item => ({
      name: item,
      value: item
    }));
  }, []);
  const modeOptions = useMemo(() => {
    const normalModes = OperatingHours.getModeEntries(value);
    return normalModes.map(([key, mode]) => {
      return {
        name: mode.name || key,
        value: key
      };
    });
  }, [value]);
  const popupTitle = useMemo(() => {
    const mode = modeOptions.find(m => m.value === currentMode);
    return mode?.name || t('selectSchedule', 'Select a schedule');
  }, [currentMode, modeOptions]);
  const onAddMode = useCallback(({
    value: modeValue
  }) => {
    const camel = titleCaseToCamelCase(modeValue);
    let nValue = OperatingHours.setMode(value, camel, WeeklyHours.create({
      name: modeValue
    }));
    const modes = OperatingHours.getModeEntries(nValue);
    if (modes.length === 1) {
      nValue = OperatingHours.setCurrentMode(nValue, camel);
    }
    setCurrentMode(camel);
    pOnChange({
      value: nValue
    });
  }, [value, pOnChange]);
  const onOpenAddMode = useCallback(() => {
    Dialogs.show({
      component: AddModeDialog,
      props: {
        name: t('addModeName'),
        modes,
        onChange: onAddMode
      }
    });
  }, [modes, onAddMode]);
  const onDeleteMode = useCallback(({
    data
  }) => {
    let nValue = OperatingHours.removeMode(value, data);
    if (currentMode === data) {
      const modes = OperatingHours.getModeEntries(nValue);
      if (modes.length === 0) {
        nValue = OperatingHours.setCurrentMode(nValue, null);
        setCurrentMode(null);
      } else {
        nValue = OperatingHours.setCurrentMode(nValue, modes[0][0]);
        setCurrentMode(modes[0][0]);
      }
    }
    pOnChange({
      value: nValue
    });
  }, [pOnChange, value, currentMode]);
  const onOpenDeleteMode = useCallback(({
    data
  }) => {
    Dialogs.show({
      component: DeleteModeDialog,
      props: {
        onChange: onDeleteMode,
        mode: data,
        modes
      }
    });
  }, [onDeleteMode, modes]);
  const onDuplicateMode = useCallback(({
    data,
    value: val
  }) => {
    let nMode = OperatingHours.getMode(value, data);
    let nextModeName = `${data}Copy`;
    let possibleMode = OperatingHours.getMode(value, nextModeName);
    if (possibleMode != null) {
      let index = 0;
      do {
        index += 1;
        nextModeName = `${data}Copy${index}`;
        possibleMode = OperatingHours.getMode(value, nextModeName);
      } while (possibleMode != null);
    }
    nMode = WeeklyHours.setName(nMode, StringUtil.camelCaseToTitleCase(nextModeName));
    const nValue = OperatingHours.setMode(value, nextModeName, nMode);
    pOnChange({
      value: nValue
    });
  }, [value, pOnChange]);
  const onRenameMode = useCallback(({
    data,
    value: val
  }) => {
    const oldModeKey = titleCaseToCamelCase(data);
    const newModeKey = titleCaseToCamelCase(val);
    let mode = OperatingHours.getMode(value, oldModeKey);
    mode = WeeklyHours.setName(mode, StringUtil.camelCaseToTitleCase(newModeKey));
    let nValue = OperatingHours.removeMode(value, oldModeKey);
    nValue = OperatingHours.setMode(nValue, newModeKey, mode);
    const currentMode = OperatingHours.getCurrentMode(nValue);
    if (currentMode === oldModeKey) {
      nValue = OperatingHours.setCurrentMode(nValue, newModeKey);
      setCurrentMode(newModeKey);
    }
    pOnChange({
      value: nValue
    });
  }, [value, pOnChange]);
  const onOpenRenameMode = useCallback(({
    value
  }) => {
    Dialogs.show({
      component: AddModeDialog,
      props: {
        name: t('addModeName', {
          context: 'update'
        }),
        onChange: onRenameMode,
        mode: value.name,
        modes,
        usage: 'update',
        eventData: value.name
      }
    });
  }, [modes, onRenameMode]);
  const onChangeMode = useCallback(({
    data,
    value: val
  }) => {
    const nValue = OperatingHours.setMode(value, data, val);
    pOnChange({
      value: nValue
    });
  }, [pOnChange, value]);
  const currentModeModel = OperatingHours.getMode(value, currentMode);
  const modeEditor = /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "operatingHours"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('mode')
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "rowL",
    align: "center"
  }, /*#__PURE__*/React.createElement(Popup, {
    title: popupTitle,
    variant: "scheduleSelector|actionBar",
    toggleVariant: "selector"
  }, modeOptions.map(mode => /*#__PURE__*/React.createElement(Popup.Item, {
    eventData: "setCurrentMode",
    onClick: onModeChange,
    checkStyle: "radio",
    value: mode.value,
    checked: mode.value === currentMode,
    key: mode.value
  }, mode.name, /*#__PURE__*/React.createElement(Popup.Item, {
    onClick: onOpenRenameMode,
    value: mode,
    eventData: mode.value,
    checkStyle: "custom",
    checkIconOff: tIcon('updateIcon', 'fa fa-fw fa-pencil')
  }, t('updateModeNameTitle')), /*#__PURE__*/React.createElement(Popup.Item, {
    onClick: onDuplicateMode,
    value: mode,
    eventData: mode.value,
    checkStyle: "custom",
    checkIconOff: tIcon('copyIcon', 'fa fa-fw fa-clone')
  }, t('copyModeTitle')), /*#__PURE__*/React.createElement(Popup.Item, {
    onClick: onOpenDeleteMode,
    eventData: mode.value,
    checkStyle: "custom",
    checkIconOff: tIcon('deleteIcon', 'fa fa-fw fa-trash')
  }, t('deleteModeTitle')))), /*#__PURE__*/React.createElement(Popup.Item, {
    checkStyle: "custom",
    onClick: onOpenAddMode,
    checkIconOff: tIcon('addIcon', 'fa fa-fw fa-plus')
  }, t('addMode'))), /*#__PURE__*/React.createElement(Flex, {
    variant: "rowM"
  }, /*#__PURE__*/React.createElement(Label, null, "Set Active"), /*#__PURE__*/React.createElement(Toggle, {
    eventData: OperatingHours.getCurrentMode(value) === currentMode ? null : currentMode,
    variant: "operatingHoursModeActive",
    value: currentMode != null && OperatingHours.getCurrentMode(value) === currentMode,
    onChange: onCurrentModeChange
  })))), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    fill: true
  }, ModelEditorFactory.create(currentModeModel, {
    onChange: onChangeMode,
    eventData: currentMode
  })));
  return /*#__PURE__*/React.createElement(Content, {
    domRef: ref,
    variant: "operatingHoursEditor",
    relative: true
  }, /*#__PURE__*/React.createElement(Content.Header, {
    variant: "operatingHoursEditorHeader"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "operatingHoursTitle|body"
  }, t('titleMessage', {
    name: blueprint?.name || 'location'
  })), /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "operatingHoursTimezone|operatingHours"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('selectTimezone')
  }, /*#__PURE__*/React.createElement(Selector, {
    showFilter: true,
    onChange: onChange,
    value: OperatingHours.getTimezone(value),
    eventData: "setTimezone",
    values: timezones
  })))), /*#__PURE__*/React.createElement(Content.Area, {
    variant: "operatingHoursEditorContent"
  }, width > MIN_TABBED_WIDTH ? /*#__PURE__*/React.createElement(Flex, {
    className: classnames(S.flex)
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(S.verticalGridCell, S.grow)
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "sectionTitle"
  }, t('hours')), modeEditor), /*#__PURE__*/React.createElement("div", {
    className: S.overrides
  }, /*#__PURE__*/React.createElement(OverridesEditor, {
    model: OperatingHours.overrides(),
    value: value,
    eventData: "setOverrides",
    onChange: pOnChange
  }))) : /*#__PURE__*/React.createElement(TabPane, {
    className: S.tab
  }, /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "hours",
    tabName: "Hours"
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(S.verticalGridCell, S.grow)
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "sectionTitle"
  }, t('hours')), modeEditor)), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "overrides",
    tabName: "Overrides"
  }, /*#__PURE__*/React.createElement("div", {
    className: S.overrides
  }, /*#__PURE__*/React.createElement(OverridesEditor, {
    model: OperatingHours.overrides(),
    value: value,
    eventData: "setOverrides",
    onChange: pOnChange
  }))))));
}
function titleCaseToCamelCase(string = '') {
  const parts = string.split(/\s/g);
  const capped = parts.map((item, index) => {
    return item.charAt(0)[index === 0 ? 'toLowerCase' : 'toUpperCase']() + item.slice(1);
  });
  return capped.join('');
}